/* TanStack Query hook for telemetry readings.
 *
 * Endpoint (per vertical-aq plugin contract):
 *   GET /readings/:device_id?from=<iso>&to=<iso>
 *
 * Returns columnar wide rows (per CLAUDE.md invariant #2). Each row is a
 * single timestamp with all sensor values as columns plus an `extras` Map
 * for less-common sensors (the ADR-029 sensor catalog drives which
 * sensors are first-class columns vs in `extras`). */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface ReadingRow {
  /** Unix milliseconds (server emits ms; chart code converts to seconds). */
  ts: number;
  /** First-class AQ sensor columns. Values are nullable (sensor missing
   *  on a given reading is legitimate; we preserve nulls rather than
   *  imputing zeros). */
  temperature_c?: number | null;
  humidity_pct?: number | null;
  co2_ppm?: number | null;
  voc_index?: number | null;
  pm25_ug_m3?: number | null;
  pm10_ug_m3?: number | null;
  hcho_ppb?: number | null;
  /** Less-common / vertical-flexible sensors. */
  extras?: Record<string, number | null>;
}

export interface ReadingsResponse {
  device_id: string;
  from: string;
  to: string;
  rows: ReadingRow[];
}

interface UseReadingsArgs {
  deviceId: string | undefined;
  from?: string;
  to?: string;
  /** Optional refetch interval (ms). Off by default. */
  refetchInterval?: number | false;
}

export function useReadings(args: UseReadingsArgs): UseQueryResult<ReadingsResponse> {
  const { deviceId, from, to, refetchInterval = false } = args;
  return useQuery<ReadingsResponse>({
    queryKey: ['readings', deviceId, from, to],
    enabled: !!deviceId,
    refetchInterval,
    queryFn: () => {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      return api.get<ReadingsResponse>(`/readings/${deviceId}${qs ? `?${qs}` : ''}`);
    },
  });
}

/** Helper: extract one sensor's series as `(x: seconds, y: number)` arrays,
 *  filtering out null values. The x-axis is Unix SECONDS (NOT ms) per the
 *  uplot-charts rule. */
export function extractSeries(
  rows: ReadingRow[],
  sensor: keyof Omit<ReadingRow, 'ts' | 'extras'> | string,
): { x: number[]; y: number[] } {
  const x: number[] = [];
  const y: number[] = [];
  for (const r of rows) {
    let v: number | null | undefined;
    if (sensor in r) {
      v = (r as unknown as Record<string, number | null | undefined>)[sensor];
    } else {
      v = r.extras?.[sensor];
    }
    if (v == null || !Number.isFinite(v)) continue;
    x.push(Math.floor(r.ts / 1000));
    y.push(v);
  }
  return { x, y };
}
